// reviewerApi.ts — API utilities for the reviewer/community review system

const API_ROOT =
  (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_URL) || '';

const normalisedRoot = API_ROOT ? API_ROOT.replace(/\/$/, '') : '';

/** Base URL for reviewer API endpoints */
export const REVIEWER_API_BASE = normalisedRoot
  ? `${normalisedRoot}/api`
  : '/api';

const isAbsoluteBase =
  REVIEWER_API_BASE.startsWith('http://') ||
  REVIEWER_API_BASE.startsWith('https://');

/** Build a reviewer API URL (relative or absolute depending on config) */
export const reviewerApiUrl = (path: string = ''): string => {
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  return `${REVIEWER_API_BASE}${normalisedPath}`;
};

/** Resolve to a fully-qualified URL (safe for fetch in both SSR and browser) */
export const resolveReviewerApiUrl = (path: string = ''): string => {
  const url = reviewerApiUrl(path);
  if (isAbsoluteBase || typeof window === 'undefined') {
    return url;
  }
  return new URL(url, window.location.origin).toString();
};

// ── Token management ────────────────────────────────────────

const TOKEN_KEY = 'userToken';
const LEGACY_TOKEN_KEY = 'reviewerToken'; // migrated below
const PENDING_REVIEW_KEY = 'pendingReview';
const PENDING_PHOTOS_KEY = 'pendingPhotos';

/** Migrate old 'reviewerToken' key to 'userToken' on first access. */
function migrateToken(): void {
  if (typeof window === 'undefined') return;
  const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacy && !localStorage.getItem(TOKEN_KEY)) {
    localStorage.setItem(TOKEN_KEY, legacy);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  }
}

export const getUserToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  migrateToken();
  return localStorage.getItem(TOKEN_KEY);
};

/** Backward-compat alias */
export const getReviewerToken = getUserToken;

export const setUserToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.removeItem(LEGACY_TOKEN_KEY); // clean up legacy key if present
};

/** Backward-compat alias */
export const setReviewerToken = setUserToken;

export const clearUserToken = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
};

/** Backward-compat alias */
export const clearReviewerToken = clearUserToken;

// ── Pending submission persistence (for progressive auth) ───

export const savePendingReview = (data: {
  entityId: number;
  entityName: string;
  formData: Record<string, unknown>;
}): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PENDING_REVIEW_KEY, JSON.stringify(data));
};

export const getPendingReview = (): {
  entityId: number;
  entityName: string;
  formData: Record<string, unknown>;
} | null => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_REVIEW_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingReview = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_REVIEW_KEY);
};

export const savePendingPhotos = (data: {
  entityId: number;
  captions: string[];
}): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PENDING_PHOTOS_KEY, JSON.stringify(data));
};

export const getPendingPhotos = (): {
  entityId: number;
  captions: string[];
} | null => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_PHOTOS_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearPendingPhotos = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_PHOTOS_KEY);
};

// ── Authenticated fetch ─────────────────────────────────────

/** fetch() wrapper that attaches the user Bearer token */
export const fetchWithReviewerAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getUserToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, { ...options, headers });
};

// ── OAuth helpers ───────────────────────────────────────────

export type ReviewerAuthProvider = 'google' | 'facebook' | 'shopify';
/** Alias for the renamed type */
export type UserAuthProvider = ReviewerAuthProvider;

/**
 * Kick off the OAuth flow for user authentication.
 * The backend returns an auth URL which we redirect the browser to.
 */
export const beginUserOAuth = async (
  provider: UserAuthProvider,
  returnUrl?: string
): Promise<void> => {
  if (typeof window === 'undefined') {
    throw new Error('OAuth sign-in can only run in the browser.');
  }

  const currentUrl = window.location.href;
  const desiredReturn = returnUrl || currentUrl;

  const endpoint = resolveReviewerApiUrl(`/user/auth/${provider}/login`);
  const loginUrl = new URL(endpoint);
  loginUrl.searchParams.set('return_url', desiredReturn);

  const response = await fetch(loginUrl.toString(), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  const payload = await response.json().catch(() => ({
    success: false,
    error: 'Unable to parse authentication response.',
  }));

  if (!response.ok || !payload.success || !payload.auth_url) {
    throw new Error(
      payload.error || 'Failed to start sign-in. Please try again.'
    );
  }

  // Store OAuth state so we can resume after redirect
  sessionStorage.setItem('user_oauth_provider', provider);
  sessionStorage.setItem('user_oauth_return', desiredReturn);

  window.location.href = payload.auth_url;
};

/** Backward-compat alias */
export const beginReviewerOAuth = beginUserOAuth;

/**
 * Create an anonymous user and return a short-lived JWT token.
 * No OAuth redirect needed — the token is returned immediately.
 */
export const authenticateAnonymous = async (): Promise<{
  token: string;
  reviewer: Record<string, unknown>;
}> => {
  const endpoint = resolveReviewerApiUrl('/user/auth/anonymous');
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || 'Failed to create anonymous session');
  }

  return { token: data.token, reviewer: data.reviewer };
};

/**
 * Sign in with Apple using a web identity token.
 * Sends the Apple-issued JWT to the backend, stores the returned session token,
 * and reloads the page to apply the authenticated state.
 */
export const signInWithAppleWeb = async (
  idToken: string,
  fullName?: string
): Promise<void> => {
  const endpoint = resolveReviewerApiUrl('/user/auth/apple/callback');
  const body: Record<string, string> = { identity_token: idToken };
  if (fullName) body.full_name = fullName;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({ success: false }));

  if (!response.ok || !data.success || !data.token) {
    throw new Error(data.error || 'Apple Sign In failed. Please try again.');
  }

  setUserToken(data.token);
  window.location.reload();
};

// ── Public API helpers ──────────────────────────────────────

/** Safely parse JSON — returns null instead of throwing on empty / invalid body */
const safeJson = async (res: Response) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/** Fetch approved reviews for an entity (public, no auth) */
export const fetchEntityReviews = async (
  entityId: number,
  page = 1,
  perPage = 10,
  sort = 'recent'
) => {
  const url = resolveReviewerApiUrl(
    `/entities/${entityId}/reviews?page=${page}&per_page=${perPage}&sort=${sort}`
  );
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch reviews');
  return (await safeJson(res)) ?? { reviews: [], total: 0, pages: 0 };
};

/** Fetch review summary/aggregates for an entity (public) */
export const fetchReviewSummary = async (entityId: number) => {
  const url = resolveReviewerApiUrl(`/entities/${entityId}/review-summary`);
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch review summary');
  return (await safeJson(res)) ?? { summary: null };
};

/** Fetch AI-generated review summary for an entity (public, cached) */
export const fetchAIReviewSummary = async (entityId: number) => {
  try {
    const url = resolveReviewerApiUrl(`/entities/${entityId}/review-ai-summary`);
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await safeJson(res)) ?? null;
  } catch {
    // AI summary is optional — don't break the page if it fails
    return null;
  }
};

/** Fetch approved visitor insights for an entity (public) */
export const fetchCommunityInsights = async (entityId: number) => {
  try {
    const url = resolveReviewerApiUrl(`/entities/${entityId}/community-insights`);
    const res = await fetch(url);
    if (!res.ok) return { insights: [], total: 0 };
    return (await safeJson(res)) ?? { insights: [], total: 0 };
  } catch {
    // Insights are optional — don't break the page if the fetch fails
    return { insights: [], total: 0 };
  }
};

/** Fetch approved community images for an entity (public) */
export const fetchCommunityImages = async (
  entityId: number,
  page = 1,
  perPage = 20
) => {
  const url = resolveReviewerApiUrl(
    `/entities/${entityId}/community-images?page=${page}&per_page=${perPage}`
  );
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch community images');
  return (await safeJson(res)) ?? { images: [], total: 0 };
};

// ── Authenticated API helpers ───────────────────────────────

/** Submit a new review (requires auth) */
export const submitReview = async (
  entityId: number,
  data: Record<string, unknown>
) => {
  const url = resolveReviewerApiUrl(`/entities/${entityId}/reviews`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Submit failed' }));
    throw new Error(err.error || 'Failed to submit review');
  }
  return res.json();
};

/** Toggle "helpful" vote on a review — no auth required */
export const toggleHelpful = async (reviewId: string) => {
  const url = resolveReviewerApiUrl(`/reviews/${reviewId}/helpful`);
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error('Failed to toggle helpful');
  return res.json();
};

/** Fetch current user's own reviews */
export const fetchMyReviews = async (page: number = 1, perPage: number = 20) => {
  const url = resolveReviewerApiUrl(`/user/my-reviews?page=${page}&per_page=${perPage}`);
  const res = await fetchWithReviewerAuth(url);
  if (!res.ok) throw new Error('Failed to fetch reviews');
  return res.json();
};

/** Update an existing review */
export const updateReview = async (
  reviewId: string,
  data: {
    title: string;
    content: string;
    visit_date?: string;
    facilities_rating?: number | null;
    cleanliness_rating?: number | null;
    value_rating?: number | null;
    location_rating?: number | null;
    accessibility_rating?: number | null;
    safety_rating?: number | null;
    pet_friendliness_rating?: number | null;
    family_friendliness_rating?: number | null;
  }
) => {
  const url = resolveReviewerApiUrl(`/user/reviews/${reviewId}`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update review');
  return res.json();
};

/** Get a presigned S3 URL for uploading a review image */
export const getReviewImageUploadUrl = async (
  reviewId: string,
  filename: string,
  contentType: string
) => {
  const url = resolveReviewerApiUrl(`/reviews/${reviewId}/images/upload-url`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) throw new Error('Failed to get upload URL');
  return res.json();
};

/** Register a review image in the database after it has been uploaded to S3 */
export const registerReviewImage = async (
  reviewId: string,
  imageData: {
    image_src: string;
    sort_order?: number;
    content_type?: string;
    file_size?: number;
  }
) => {
  const url = resolveReviewerApiUrl(`/reviews/${reviewId}/images`);
  const res = await fetchWithReviewerAuth(url, {
    method: 'POST',
    body: JSON.stringify(imageData),
  });
  if (!res.ok) throw new Error('Failed to register review image');
  return res.json();
};

/**
 * Direct multipart upload for a community photo (build 244 #3).
 *
 * Replaces the legacy 3-step S3 presigned-URL dance:
 *   1. POST /community-images/upload-url → presigned PUT URL  (DELETED)
 *   2. PUT to S3 with the file                                (BROKEN — bucket gone)
 *   3. POST /community-images with the URL → register in DB   (DELETED)
 *
 * The S3 bucket `client.trip-planner.com.au` was removed post-Hetzner
 * migration; the legacy endpoints have been deleted in the backend.
 * One round-trip now: server writes the file to a Docker volume
 * shared with Caddy, registers the CommunityImage Neo4j node, and
 * returns the created record. Caddy serves the file straight from
 * disk at /media/community-images/{entity_id}/{uuid}.{ext}.
 */
export const uploadCommunityImage = async (
  entityId: number,
  file: File | Blob,
  caption?: string
) => {
  const url = resolveReviewerApiUrl(
    `/entities/${entityId}/community-images/upload`
  );
  const form = new FormData();
  // Preserve the filename when available — server uses content type
  // for extension picking but the field is required by the spec.
  if (file instanceof File) {
    form.append('image', file, file.name);
  } else {
    form.append('image', file, 'photo.jpg');
  }
  if (caption !== undefined && caption !== '') {
    form.append('caption', caption);
  }
  const res = await fetchWithReviewerAuth(url, { method: 'POST', body: form });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error ? `: ${body.error}` : '';
    } catch {
      /* ignore */
    }
    throw new Error(`Failed to upload community image (HTTP ${res.status})${detail}`);
  }
  return res.json();
};

/** Get current user profile */
export const fetchUserProfile = async () => {
  const url = resolveReviewerApiUrl('/user/me');
  const res = await fetchWithReviewerAuth(url);
  if (!res.ok) return null;
  return res.json();
};

/**
 * Like fetchUserProfile but surfaces the HTTP status so callers can tell a
 * genuine 401 (invalid/expired token → log out) from a transient 5xx / network
 * error (a Neo4j blip → keep the session). fetchUserProfile collapses both to
 * null, which caused users to be logged out on a momentary DB hiccup.
 * `status` is 0 when the request never got a response (network error).
 */
export const fetchUserProfileResult = async (): Promise<{ status: number; profile: unknown | null }> => {
  const url = resolveReviewerApiUrl('/user/me');
  const res = await fetchWithReviewerAuth(url);
  if (!res.ok) return { status: res.status, profile: null };
  const profile = await res.json().catch(() => null);
  return { status: res.status, profile };
};

/** Backward-compat alias */
export const fetchReviewerProfile = fetchUserProfile;
